import i18n from "../i18n";
import { classifyNftContract, getNftCapabilityMessageKey } from "./nftCapabilities";

const TRANSFER_METHODS = new Set([
  "nft_transfer",
  "nft_transfer_call",
  "nft_batch_transfer",
  "nft_batch_transfer_call",
]);

function getFunctionCall(action) {
  if (!action || typeof action !== "object") return null;
  if (String(action.type || "") !== "FunctionCall") return null;
  const params = action.params || {};
  const methodName = String(params.methodName || params.method_name || "").trim();
  if (!methodName) return null;
  return {
    methodName,
    args: params.args && typeof params.args === "object" ? params.args : {},
  };
}

function capabilityError(capability, action) {
  const messageKey = getNftCapabilityMessageKey(capability) || "nftCapability.blockedGeneric";
  const localized = i18n.t(messageKey, { ns: "wallet" });
  const error = new Error(localized || "NFT_CAPABILITY_BLOCKED");
  error.code = "NFT_CAPABILITY_BLOCKED";
  error.action = action;
  error.messageKey = messageKey;
  error.capability = capability;
  return error;
}

export async function assertTransactionAllowed(transaction, { classifier = classifyNftContract } = {}) {
  const receiverId = String(transaction?.receiverId || transaction?.receiver_id || "")
    .trim()
    .toLowerCase();
  const actions = Array.isArray(transaction?.actions) ? transaction.actions : [];

  for (const action of actions) {
    const call = getFunctionCall(action);
    if (!call) continue;

    if (TRANSFER_METHODS.has(call.methodName)) {
      const capability = await classifier(receiverId, { force: true });
      if (capability?.canSend !== true || capability?.verified !== true) {
        throw capabilityError(capability, "send");
      }
      continue;
    }

    if (call.methodName === "nft_approve") {
      const capability = await classifier(receiverId, { force: true });
      if (capability?.canApproveMarket !== true || capability?.verified !== true) {
        throw capabilityError(capability, "approve");
      }
      continue;
    }

    if (call.methodName === "offer_nft") {
      const nftContractId = String(call.args?.nft_contract_id || call.args?.nftContractId || "")
        .trim()
        .toLowerCase();
      if (!nftContractId) throw capabilityError(null, "sell");
      const capability = await classifier(nftContractId, { force: true });
      if (capability?.canSell !== true || capability?.verified !== true) {
        throw capabilityError(capability, "sell");
      }
    }
  }
}

const guardedWallets = new WeakMap();
const guardedConnectors = new WeakMap();

function wrapWallet(wallet, classifier = classifyNftContract) {
  if (!wallet || typeof wallet !== "object") return wallet;
  if (guardedWallets.has(wallet)) return guardedWallets.get(wallet);

  const guarded = new Proxy(wallet, {
    get(target, property) {
      if (property === "signAndSendTransaction") {
        const original = target[property];
        if (typeof original !== "function") return original;
        return async (transaction) => {
          await assertTransactionAllowed(transaction, { classifier });
          return original.call(target, transaction);
        };
      }
      if (property === "signAndSendTransactions") {
        const original = target[property];
        if (typeof original !== "function") return original;
        return async (payload) => {
          const transactions = Array.isArray(payload)
            ? payload
            : Array.isArray(payload?.transactions)
              ? payload.transactions
              : [];
          for (const transaction of transactions) {
            await assertTransactionAllowed(transaction, { classifier });
          }
          return original.call(target, payload);
        };
      }
      const value = target[property];
      return typeof value === "function" ? value.bind(target) : value;
    },
  });

  guardedWallets.set(wallet, guarded);
  return guarded;
}

export function createCapabilityGuardedConnector(connector, { classifier = classifyNftContract } = {}) {
  if (!connector || typeof connector !== "object") return connector;
  if (guardedConnectors.has(connector)) return guardedConnectors.get(connector);

  const guarded = new Proxy(connector, {
    get(target, property) {
      if (property === "wallet") {
        const original = target[property];
        if (typeof original !== "function") return original;
        return async (...args) => wrapWallet(await original.apply(target, args), classifier);
      }
      const value = target[property];
      return typeof value === "function" ? value.bind(target) : value;
    },
  });

  guardedConnectors.set(connector, guarded);
  return guarded;
}
